import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { saveAs } from 'file-saver';
import { AppService } from '../../../../../shared/services';
import {
  IssueWorkflowChange,
} from '../../../../../shared/components/issue-workflow/issue-workflow-panel/issue-workflow-panel.component';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'svg'];
const VIDEO_EXTS = ['mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'];
const DOCUMENT_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

function deriveFileType(item: any): 'picture' | 'video' | 'document' {
  if (item?.fileType) return item.fileType;
  const mime = (item?.mime || item?.mime_type || item?.content_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'picture';
  if (mime.startsWith('video/')) return 'video';
  if (mime) return 'document';
  const name = (item?.original_name || item?.name || item?.filename || item?.original_url || '').toLowerCase();
  const ext = name.split('?')[0].split('.').pop() || '';
  if (IMAGE_EXTS.includes(ext)) return 'picture';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (DOCUMENT_EXTS.includes(ext)) return 'document';
  return 'picture';
}

function normalizeMedia(item: any): any {
  const fileType = deriveFileType(item);
  return {
    ...item,
    fileType,
    preview_url: item.preview_url || item.preview || item.original_url || item.thumb_url || '',
  };
}

@Component({
  selector: 'app-issue-detail-sidenav',
  templateUrl: './issue-detail-sidenav.component.html',
  styleUrls: ['./issue-detail-sidenav.component.scss'],
  standalone: false,
})
export class IssueDetailSidenavComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;
  @Input() issue: any;
  @Output() closed = new EventEmitter<void>();

  loadedIssue: any = null;

  get mediaItems(): any[] {
    const all = [
      ...((this.loadedIssue?.pictures as any[]) || []),
      ...((this.loadedIssue?.attachments as any[]) || []),
    ];
    return all.map(normalizeMedia);
  }

  get pictureItems(): any[] {
    return this.mediaItems.filter(m => m.fileType === 'picture');
  }

  downloadAllPictures(): void {
    const pics = this.pictureItems;
    if (!pics.length) return;
    pics.forEach((pic, idx) => {
      const url = pic.original_url || pic.thumb_url;
      if (!url) return;
      const name = pic.original_name || `issue-${idx + 1}.jpg`;
      saveAs(url, name);
    });
  }

  get subHeaderChips(): string[] {
    const chips: string[] = [];
    const loc = this.loadedIssue?.site?.name || this.loadedIssue?.location;
    if (loc) chips.push(loc);
    const date = this.loadedIssue?.created_at;
    if (date) {
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          chips.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
        }
      } catch (e) { /* ignore */ }
    }
    return chips;
  }

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    open:     { label: 'Open',     bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    resolved: { label: 'Resolved', bgColor: 'bg-approved',  textColor: 'txt-approved' },
    closed:   { label: 'Closed',   bgColor: 'bg-draft',     textColor: 'txt-draft' },
    // Contractor-workflow stages (projects with issue_workflow_enabled): the
    // one status pill of the issue, in the stage chip's colours.
    sent_to_contractor: { label: 'Forwarded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
    responded:          { label: 'Responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  };

  private readonly onDestroy = new Subject<void>();

  constructor(
    public appService: AppService,
  ) {}

  ngOnInit() {
    this.loadedIssue = this.normalizeIssue(this.issue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.issue && !changes.issue.firstChange) {
      this.loadedIssue = this.normalizeIssue(this.issue);
    }
  }

  private normalizeIssue(issue: any): any {
    if (!issue) return null;
    const merged = { ...issue, ...(issue.attributes || {}) };
    return {
      ...merged,
      pictures: (merged.pictures || []).map(normalizeMedia),
      attachments: (merged.attachments || []).map(normalizeMedia),
    };
  }

  /**
   * A contractor-workflow action went through (only raised on a project with
   * issue_workflow_enabled): take the status from the updated issue.
   */
  onWorkflowChange(change: IssueWorkflowChange): void {
    const issue = change?.issue;
    if (!issue || !this.loadedIssue) {
      return;
    }
    this.loadedIssue = {
      ...this.loadedIssue,
      status: issue.status,
    };
  }

  onClose(): void {
    this.closed.emit();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
